import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'

type VerificationCodeInputProps = {
  length?: number
  value: string
  onChange: (value: string) => void
  error?: boolean
  disabled?: boolean
  className?: string
}

const getDigits = (value: string, length: number) =>
  Array.from({ length }, (_, index) => value[index] ?? '')

export function VerificationCodeInput({
  length = 6,
  value,
  onChange,
  error = false,
  disabled = false,
  className,
}: VerificationCodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (!disabled) {
      inputsRef.current[0]?.focus()
    }
  }, [disabled])

  const digits = useMemo(() => getDigits(value, length), [value, length])

  const handleChange = (index: number, nextValue: string) => {
    const sanitized = nextValue.replace(/\D/g, '')
    const nextChar = sanitized.slice(-1)
    const nextDigits = [...digits]
    nextDigits[index] = nextChar
    onChange(nextDigits.join(''))

    if (nextChar && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') {
      return
    }

    if (digits[index]) {
      return
    }

    if (index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return
    }

    const pasted = event.clipboardData.getData('text')
    const nextDigits = pasted.replace(/\D/g, '').slice(0, length).split('')
    if (nextDigits.length === 0) {
      return
    }

    const filled = Array.from({ length }, (_, index) => nextDigits[index] ?? '')
    onChange(filled.join(''))
    const lastIndex = Math.min(nextDigits.length, length) - 1
    inputsRef.current[lastIndex]?.focus()
    event.preventDefault()
  }

  const cellStyle: CSSProperties = {
    background: designTokens.colors.background.inputDefault,
    borderColor: error ? designTokens.colors.semantic.error : designTokens.colors.border.default,
    borderRadius: designTokens.borders.radius.md,
    color: designTokens.colors.text.primary,
    ...typographyStyle(designTokens.typography.input.codePlaceholder),
  }

  return (
    <div className={cn('flex flex-col gap-2', className)} onPaste={handlePaste}>
      <div className="grid grid-cols-6 gap-3">
        {digits.map((digit, index) => (
          <input
            key={`code-cell-${index}`}
            ref={(node) => {
              inputsRef.current[index] = node
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            disabled={disabled}
            value={digit}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            className={cn(
              'h-12 w-full rounded-md border text-center outline-none transition focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              disabled ? 'cursor-not-allowed opacity-50' : 'bg-background'
            )}
            style={cellStyle}
          />
        ))}
      </div>
    </div>
  )
}
